import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import type { TSessionAnalytics } from "@/types/TAnalytics";
import { useSessions } from "@/hooks/dashboard/useSessions";
import { SessionsTable } from "@/components/dashboard/heygen-account/sessions-info/table/SessionsTable";
import { SessionsHeader } from "@/components/dashboard/heygen-account/sessions-info/SessionsHeader";
import { SessionsPagination } from "@/components/dashboard/heygen-account/sessions-info/SessionsPagination";
import { SessionsLoading } from "@/components/dashboard/heygen-account/sessions-info/SessionsLoading";

type SessionsAnalyticsProps = {
  data: TSessionAnalytics;
};

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function SessionsDailyChart({ data }: { data: TSessionAnalytics }) {
  const { t } = useTranslation();

  const chartData = data.by_day.map(d => ({
    date: new Date(d.date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }),
    sessions: d.sessions,
    duration: Math.round(d.duration / 60),
  }));

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        {t("analytics.noData", "Keine Daten verfügbar")}
      </div>
    );
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip
            contentStyle={{
              backgroundColor: "var(--tooltip-bg, #fff)",
              border: "1px solid var(--tooltip-border, #e5e7eb)",
              borderRadius: "0.5rem",
            }}
          />
          <Legend />
          <Bar
            dataKey="sessions"
            name={t("analytics.sessions.sessionsPerDay", "Sessions")}
            fill="#8b5cf6"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function CreditsChart({ data }: { data: TSessionAnalytics }) {
  const { t } = useTranslation();

  const chartData = data.by_day.map(d => ({
    date: new Date(d.date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }),
    credits: d.credits,
  }));

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        {t("analytics.noData", "Keine Daten verfügbar")}
      </div>
    );
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip
            contentStyle={{
              backgroundColor: "var(--tooltip-bg, #fff)",
              border: "1px solid var(--tooltip-border, #e5e7eb)",
              borderRadius: "0.5rem",
            }}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="credits"
            name={t("analytics.sessions.creditsPerDay", "Credits")}
            stroke="#f59e0b"
            strokeWidth={2}
            dot={{ fill: "#f59e0b", r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function SessionsTableSection() {
  const { t } = useTranslation();
  const {
    sessionsResponse,
    isLoading,
    pageSize,
    statusFilter,
    actions,
  } = useSessions(10);

  const hasData = sessionsResponse?.data;

  return (
    <div className="space-y-2">
      <SessionsHeader
        statusFilter={statusFilter}
        onStatusChange={actions.setStatusFilter}
        pageSize={pageSize}
        onPageSizeChange={actions.setPageSize}
        onRefresh={actions.refetch}
      />
      {isLoading ? (
        <SessionsLoading />
      ) : hasData ? (
        <>
          <SessionsTable data={sessionsResponse.data} onRefresh={actions.refetch} />
          <SessionsPagination
            sessionsResponse={sessionsResponse}
            onPreviousPage={actions.previousPage}
            onNextPage={actions.nextPage}
            onPageChange={actions.goToPage}
          />
        </>
      ) : (
        <div className="text-center py-4 text-gray-500 text-sm">
          {t("analytics.noData", "Keine Daten verfügbar")}
        </div>
      )}
    </div>
  );
}

export function SessionsAnalytics({ data }: SessionsAnalyticsProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      {/* Session Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{data.total}</p>
          <p className="text-xs text-purple-600 dark:text-purple-400">{t("analytics.sessions.total", "Gesamt")}</p>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{data.active}</p>
          <p className="text-xs text-blue-600 dark:text-blue-400">{t("analytics.sessions.active", "Aktiv")}</p>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{data.total_credits.toFixed(2)}</p>
          <p className="text-xs text-amber-600 dark:text-amber-400">{t("analytics.sessions.credits", "Credits")}</p>
        </div>
        <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-teal-700 dark:text-teal-300">{formatDuration(data.avg_duration)}</p>
          <p className="text-xs text-teal-600 dark:text-teal-400">{t("analytics.sessions.avgDuration", "Ø Dauer")}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">
            {t("analytics.sessions.dailyChart", "Sessions pro Tag")}
          </h4>
          <SessionsDailyChart data={data} />
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">
            {t("analytics.sessions.creditsChart", "Credits-Verlauf")}
          </h4>
          <CreditsChart data={data} />
        </div>
      </div>

      {/* Sessions Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">
          {t("analytics.sessions.tableTitle", "Session-Übersicht")}
        </h4>
        <SessionsTableSection />
      </div>
    </div>
  );
}
